/**
 * 多维分析 Search 业务逻辑 Hook（PR2）
 *
 * 从 index.tsx 迁出：Sortable 列表派生、拖拽、计算/清空/SQL 预览、
 * rowLimit 与 visibleSearch、校验态等；index 仅负责 connect + 渲染。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { history } from 'umi';
import { notification } from 'tea-component';
import { useForm } from 'react-final-form-hooks';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';

import { useDblIntl } from '@/pages/datalab/utils/utils';
import { DATALAB_ANALYSIS_ERROR_CODE } from '@dbl/core/global';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import { checkAllConditions, checkRawColumnModeConditions } from '@/pages/datalab/pages/Multidimensional/utils';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import { DBColumnType } from '@/pages/datalab/components/SortableDrag/typeDefine';
import {
  TDispatchFun,
  SortableItems,
} from '@/pages/datalab/components/SortableDrag/configration';
import { confirmColumns, dragOnMove, metricOptionText } from '@/pages/datalab/components/SortableDrag/utils';
import type { IUserGroup } from '@/pages/datalab/components/UserFilter/FilterPanel/type';
import { usePreviewSQL } from '@dbl/domainComponents/dataSource';
import type { IPreviewSqlVo } from '@dbl/services/dataSource';
import { useChartSettingsSelector } from '../../ChartSettingsV2/context';
import { buildCustomizeSettingForApi } from '../../ChartSettingsV2/services/api-upload';
import { createSearchDispatchAdapter } from '../adapters/searchDispatch';
import { buildSortableAppendList } from '../utils/sortableListHelpers';
import {
  confirmClearAll,
  openDefineFieldModal,
} from '../utils/searchModalHelpers';
import type { ISearchActionsParams, ISearchActionsResult } from './types';

/** 聚合 Search 区业务逻辑与事件处理，供 index 纯渲染消费 */
export function useSearchActions(
  /** connect 注入后的 Search 全量 props */
  params: ISearchActionsParams,
): ISearchActionsResult {
  const { props } = params;
  // connect 注入的 model 字段，与 Sortable 区、计算接口一一对应
  const {
    dataTables,
    loading,
    columnFilters,
    columnCompares,
    columnMetrics,
    columnDimensions,
    columnX,
    columnShowDimensions,
    customDimensionMap,
    columnRawColumns,
    mode,
    dragState,
    task_id,
    chartType,
    rowLimit,
    save_column_infos,
    customColumns,
    dataTableJoinRelations,
    visibleSearch: visibleSearchFromStore,
  } = props;

  /** 封装 analysis model 的 save / runAnalysis 等 effect，避免 Hook 内散落 dispatch 字符串 */
  const searchAdapter = useMemo(
    () => createSearchDispatchAdapter(props.dispatch),
    [props.dispatch],
  );
  // Sortable 内部仍使用 TDispatchFun 契约，adapter 统一封装 model effect 路径
  const dispatchFun: TDispatchFun = searchAdapter.dispatchFun;

  /** 表格 + 明细列模式：隐藏指标/维度区，仅展示筛选与 raw column */
  const isRawColumnMode =
    chartType === ChartType.Table && mode === ChartCalculateMode.RawColumn;

  const {
    dblFormatMessage: multiFormatMessage,
    commonFormatMessage,
    formatMessage,
    isEn,
  } = useDblIntl('multi');

  /** 筛选区折叠态：本地 state + model 持久化，避免仅内存态丢失 */
  const [visibleSearch, setVisibleSearch] = useState(
    visibleSearchFromStore ?? true,
  );
  /** 对比项区是否曾展示（历史逻辑保留，与 columnCompares 同步） */
  const [, setShowColumnCompares] = useState<boolean>(
    columnCompares.length > 0,
  );
  /** rowLimit 超出 [2000, 50000] 时展示错误 Bubble */
  const [isRowLimitBlur, setIsRowLimitBlur] = useState(false);
  /** 最近一次合法 rowLimit，失焦回退用 */
  const [lastValidValue, setLastValidValue] = useState(0);

  /** ChartSettingsV2 Context：计算接口 customizeSetting 与 Confirm 后重算触发 */
  const recalcTriggerVersion = useChartSettingsSelector(
    s => s.recalcTriggerVersion,
  );
  const baseFromContext = useChartSettingsSelector(s => s.base);

  /** 拖拽悬停：解析目标 Sortable 区并委托公共 dragOnMove 做重复/敏感校验 */
  const dragOnMoveHandle = useCallback(
    (
      /** Sortable 目标容器 DOM 事件 */
      evt: { to: { className: string; }; },
      originalEvent: Event,
      innerDispatchFun: TDispatchFun,
      /** 当前拖拽中的字段，用于敏感字段拦截 */
      currentDragItem: IColumnInfo | null,
    ) => {
      const { to } = evt;
      const sortableClass = to.className
        .split(' ')
        .find((item: string) => item.includes(`-${SortableItems}`));
      if (!sortableClass) {
        return false;
      }
      const sortableKeyMove = sortableClass.slice(
        0,
        sortableClass.indexOf(`-${SortableItems}`),
      );

      // sortableKeyMove 与 model 字段名一致，供 dragOnMove 读取当前区已有字段做重复/敏感校验
      const columnInfoMap: Record<string, IColumnInfo[]> = {
        columnFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnX,
        columnShowDimensions,
        columnRawColumns,
      };
      const columnInfos = columnInfoMap[sortableKeyMove];

      return dragOnMove(
        evt,
        originalEvent,
        sortableKeyMove,
        columnInfos,
        innerDispatchFun,
        currentDragItem,
      );
    },
    [
      columnFilters,
      columnCompares,
      columnMetrics,
      columnDimensions,
      columnX,
      columnShowDimensions,
      columnRawColumns,
    ],
  );

  /** 打开自定义字段编辑弹窗，确认后写回 model */
  const showCustomColumnModel = useMemoizedFn((item: IColumnInfo) => {
    openDefineFieldModal({
      item,
      customColumns,
      dataTables,
      dataTableJoinRelations,
      multiFormatMessage,
      commonFormatMessage,
      isEn,
      onConfirm: customColumn => {
        searchAdapter.updateCustomColumn(customColumn);
      },
    });
  });

  /** 各 Sortable 共享的拖拽配置（canDrag / dragState / move 校验） */
  const [sortableOption, setSortableOption] = useState<ISortableOption>({
    canDrag: true,
    dragState,
    dragMoveHandle: dragOnMoveHandle,
    dispatch: props.dispatch,
  });

  // 编辑任务恢复 rowLimit：按 taskId / 中英文名称多 key 读取，避免语言切换重置
  useEffect(() => {
    if (!loading) {
      const urlSearchParamsName = (history.location?.query ||
        {}) as Record<string, string | undefined>;
      const saveRowLimits = save_column_infos?.saveRowLimits;
      const restoredRowLimit =
        saveRowLimits?.[task_id] ??
        (urlSearchParamsName.name
          ? saveRowLimits?.[urlSearchParamsName.name]
          : undefined) ??
        (urlSearchParamsName.nameCn
          ? saveRowLimits?.[urlSearchParamsName.nameCn]
          : undefined) ??
        5000;
      searchAdapter.saveRowLimit(restoredRowLimit);
    }
  }, [task_id, loading, save_column_infos?.saveRowLimits, searchAdapter]);

  useEffect(() => {
    /** 对比项变化时同步展示标记（columnCompares 非空即视为曾配置对比） */
    setShowColumnCompares(columnCompares.length > 0);
  }, [columnCompares]);

  /** dragState 变更时同步到 sortableOption，供各 Sortable 子组件消费 */
  useEffect(() => {
    setSortableOption(prev => ({
      ...prev,
      dragState,
      dragMoveHandle: dragOnMoveHandle,
    }));
  }, [dragState, dragOnMoveHandle]);

  // —— Sortable 展示列表：业务字段 + 占位尾项，column_text 不参与 save ——
  /** 筛选区拖拽列表（含占位项） */
  const appendFilters = buildSortableAppendList(columnFilters, (item: IColumnInfo) => ({
    ...item,
    column_text: DBColumnType.getFilterText(item, isEn),
  }));

  /** 指标区拖拽列表（聚合函数展示文案） */
  const appendMetrics = buildSortableAppendList(columnMetrics, (item: IColumnInfo) => ({
    ...item,
    column_text: metricOptionText(item.aggregate, isEn),
  }));

  /** 维度区拖拽列表 */
  const appendDimensions = buildSortableAppendList(
    columnDimensions,
    (item: IColumnInfo) => ({
      ...item,
      column_text: isEn
        ? item.column_display_en || item.column_display
        : item.column_display,
    }),
  );

  /** X 轴维度拖拽列表（趋势图 / 多维数值趋势） */
  const appendX = buildSortableAppendList(columnX, (item: IColumnInfo) => ({
    ...item,
    column_text: isEn
      ? item.column_display_en || item.column_display
      : item.column_display,
  }));

  /** 展示维度区（DimNumberTrend 专用） */
  const appendShowDimensions = buildSortableAppendList(
    columnShowDimensions,
    (item: IColumnInfo) => ({
      ...item,
      column_text: isEn
        ? item.column_display_en || item.column_display
        : item.column_display,
    }),
  );

  /** 明细列模式 raw column 拖拽列表 */
  const appendRawColumns = buildSortableAppendList(
    columnRawColumns,
    (item: IColumnInfo) => ({
      ...item,
      column_text: isEn
        ? item.column_display_en || item.column_display
        : item.column_display,
    }),
  );

  /** SQL 格式化方言，取自首表 driver，解析失败则原样展示 */
  const driver = dataTables?.[0]?.driver || 'mysql';

  const fetchPreviewSql = useCallback(
    (): Promise<IPreviewSqlVo> =>
      new Promise((resolve, reject) => {
        searchAdapter.previewSql((sql, err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve({ finalSql: sql ?? '' });
        });
      }),
    [searchAdapter],
  );

  const {
    previewSQL,
    loading: sqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql,
    sqlFormatLanguage: driver,
  });

  /** 点击「计算」：组装图表配置并触发 getDataAnlysis */
  const onSubmit = useMemoizedFn(() => {
    // 聚合模式下计算前确认维度列配置，避免与 save 快照不一致
    mode === ChartCalculateMode.Aggregate &&
      confirmColumns(props, dispatchFun, ['columnDimensions']);

    // 接口上传统一走 buildCustomizeSettingForApi（visibility 过滤 + 空则 omit）
    const customizeSetting = buildCustomizeSettingForApi({
      chartType: chartType as TChartType,
      base: baseFromContext as Record<string, unknown>,
      draft: {},
      columnDimensions,
      columnMetrics,
      mode,
    });

    searchAdapter.runAnalysis(
      {
        isEn,
        mode,
        isGetTableSheet: mode === ChartCalculateMode.RawColumn,
        customizeSetting,
      },
      payload => {
        /** 计算结束回调：展示业务错误并重置 analysisRunning / dragState */
        if (payload?.errorMsg && payload?.errorCode !== 40005) {
          /** 40005 为取消类错误，不重复 toast */
          notification.error({
            className: 'dbl-analysis-msg',
            description: DATALAB_ANALYSIS_ERROR_CODE?.includes(
              payload?.errorCode as number,
            )
              ? formatMessage(
                { id: `dbl_error_${payload?.errorCode || '1'}` },
                {
                  name: payload?.errorData as string,
                  msg: payload?.errorMsg as string,
                },
              )
              : (payload?.errorMsg as string),
            duration: 3000,
          });
        }

        searchAdapter.save({
          ...(payload || {}),
          analysisRunning: false,
          analysisReqId: '',
          dragState: {
            currentDrag: '',
            errorDrag: {},
          },
        });
      },
    );
  });

  /** 主表单 submit：绑定「计算」按钮 htmlType="submit" */
  const { handleSubmit, validating } = useForm({
    initialValuesEqual: () => true,
    onSubmit,
  });

  useUpdateEffect(() => {
    // ChartSettings Confirm 后 recalcTriggerVersion +1；>0 才发起重算，避免 RESET 误触发
    if (recalcTriggerVersion > 0) {
      onSubmit();
    }
  }, [recalcTriggerVersion]);

  /** 无数据表、加载中或表单校验中时禁用底部操作按钮 */
  const disableButton =
    JSON.stringify(dataTables) === '[]' || loading || validating;

  /** 清空全部筛选与图表配置，需用户二次确认 */
  const clearAll = useMemoizedFn(() => {
    confirmClearAll({ multiFormatMessage, commonFormatMessage }).then(
      confirmed => {
        if (!confirmed) {
          return;
        }
        /** 确认后清空 model 字段；ChartSettingsV2 reset 由 clearQuery / 内部链路处理 */
        searchAdapter.clearQuery({
          dragState: {
            currentDrag: '',
            errorDrag: {},
          },
        });
      },
    );
  });

  /** 终止进行中的分析任务 */
  const cancelAnalysis = useMemoizedFn(() => {
    searchAdapter.cancelAnalysis({
      /** 终止结果 toast */
      cancelTip(status: 'success' | 'error') {
        const tips = {
          success: ['Cancel calculation successful', '终止计算成功'],
          error: ['Cancel calculation failed', '终止计算失败'],
        };
        notification[status]({
          className: `downDataBox downData${status}`,
          description: tips[status][isEn ? 0 : 1],
          duration: 3000,
        });
      },
    });
  });

  /** rowLimit 失焦：非法值回退为上次合法值或默认 5000 */
  const handleBlur = useMemoizedFn(() => {
    if (lastValidValue) {
      searchAdapter.saveRowLimit(lastValidValue);
      setIsRowLimitBlur(false);
      return;
    }
    if (+rowLimit < 2000) {
      searchAdapter.saveRowLimit(5000);
      setIsRowLimitBlur(false);
    }
  });

  /** rowLimit 输入：实时写 model，超 50000 显示错误 Bubble */
  const handleRowLimitChange = useMemoizedFn((value: string) => {
    const valueNum = Number.parseInt(value, 10);
    searchAdapter.saveRowLimit(valueNum);
    if (valueNum <= 50000) {
      setIsRowLimitBlur(false);
    } else {
      setIsRowLimitBlur(true);
    }
    if (valueNum <= 50000 && valueNum >= 2000) {
      setLastValidValue(valueNum);
    }
  });

  /** 阻止 Enter 触发表单 submit，避免误触计算 */
  const handleRowLimitPressEnter = useMemoizedFn(
    (_value: string, eve: React.KeyboardEvent) => {
      eve.preventDefault();
    },
  );

  /** 用户分群筛选变更 */
  const handleUserFilter = useMemoizedFn((updatedGroups: IUserGroup[]) => {
    searchAdapter.saveUserFilters(updatedGroups as never);
  });

  /** 折叠/展开 Search 表单区，状态持久化到 model */
  const toggleVisibleSearch = useMemoizedFn(() => {
    const nextVisibleSearch = !visibleSearch;
    setVisibleSearch(nextVisibleSearch);
    searchAdapter.saveVisibleSearch(nextVisibleSearch);
  });

  /** 当前图表类型是否隐藏维度区（如部分饼图配置） */
  const hiddenDimension = ChartTypeInfos[chartType]?.dimension?.hidden;
  /** 计算前置校验：字段必填、清空态、Bubble 提示 HTML */
  const validationResult = isRawColumnMode
    ? checkRawColumnModeConditions(
      chartType,
      { columnFilters, columnRawColumns },
      isEn,
    )
    : checkAllConditions(
      chartType,
      {
        columnFilters,
        columnCompares,
        columnMetrics,
        columnDimensions,
        columnX,
        columnShowDimensions,
      },
      isEn,
    );
  const { isCheckSuccess, isCleared, bubbleContent } = validationResult;
  /** DimNumberTrend 需已配置 dimensionMap 才允许计算 */
  const hasDimensionMap =
    ChartType.DimNumberTrend === chartType
      ? (customDimensionMap?.data?.length ?? 0) > 0
      : true;

  return {
    // i18n
    multiFormatMessage,
    commonFormatMessage,
    formatMessage: formatMessage as ISearchActionsResult['formatMessage'],
    isEn,
    // dispatch
    searchAdapter,
    dispatchFun,
    // 面板显隐
    visibleSearch,
    toggleVisibleSearch,
    // 底部操作
    sqlLoading,
    isRowLimitBlur,
    // Sortable
    sortableOption,
    setSortableOption,
    appendFilters: appendFilters as IColumnInfo[],
    appendMetrics: appendMetrics as IColumnInfo[],
    appendDimensions: appendDimensions as IColumnInfo[],
    appendX: appendX as IColumnInfo[],
    appendShowDimensions: appendShowDimensions as IColumnInfo[],
    appendRawColumns: appendRawColumns as IColumnInfo[],
    showCustomColumnModel,
    handleSubmit,
    validating,
    clearAll,
    previewSQL,
    previewSqlModalVisible,
    hidePreviewSqlModal,
    previewSqlContent,
    previewSqlError,
    cancelAnalysis,
    handleBlur,
    handleRowLimitChange,
    handleRowLimitPressEnter,
    handleUserFilter,
    // 校验与图表形态
    isRawColumnMode,
    hiddenDimension,
    isCheckSuccess,
    isCleared,
    bubbleContent,
    hasDimensionMap,
    disableButton,
  };
}

